import { apiClient } from './api';
import type {
  GetSignalsApiResponse,
  GetSignalApiResponse,
  GetSignalRelatedIssuesApiResponse,
  GetMarketplaceSignalsApiResponse,
  CreateSignalApiResponse,
  UpdateSignalApiRequest,
  UpdateSignalApiResponse,
  DeleteSignalApiResponse,
  ActivateSignalApiResponse,
  DeactivateSignalApiResponse,
  ImportSignalApiResponse,
  RemoveImportedSignalApiResponse,
  ApplySignalApiResponse,
  UnapplySignalApiResponse,
  CreateSignalApiRequest,
  GetSignalsApiRequest,
  RelatedSignalsApiRequest,
  RelatedSignalsApiResponse,
  GenerateSignalsFromFileApiResponse,
} from '@momentum/types';
import type { SignalApplicabilityScope, Signal } from '@momentum/types';

export const signalService = {
  /**
   * Get all signals with optional filters
   */
  async getSignals(
    params?: GetSignalsApiRequest
  ): Promise<GetSignalsApiResponse> {
    const queryParams = new URLSearchParams();

    if (params?.interactionId) {
      queryParams.append('interactionId', params.interactionId);
    }

    if (params?.installedStatus) {
      queryParams.append('installedStatus', params.installedStatus);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/signals?${queryString}` : '/signals';

    return (await apiClient.get(url)).data;
  },

  /**
   * Get signals by IDs (single request, returns only accessible signals)
   */
  async getSignalsByIds(ids: string[]): Promise<GetSignalsApiResponse> {
    if (ids.length === 0) return [];
    const queryParams = new URLSearchParams();
    queryParams.set('ids', ids.join(','));
    return (await apiClient.get(`/signals/by-ids?${queryParams.toString()}`))
      .data;
  },

  /**
   * Get a single signal by ID
   */
  async getSignalById(id: string): Promise<GetSignalApiResponse> {
    return (await apiClient.get(`/signals/${id}`)).data;
  },

  /**
   * Create a new signal
   */
  async createSignal(
    data: CreateSignalApiRequest
  ): Promise<CreateSignalApiResponse> {
    return (await apiClient.post('/signals', data)).data;
  },

  /**
   * Delete a signal
   */
  async updateSignal(
    id: string,
    data: UpdateSignalApiRequest
  ): Promise<UpdateSignalApiResponse> {
    return (await apiClient.patch(`/signals/${id}`, data)).data;
  },

  async deleteSignal(id: string): Promise<DeleteSignalApiResponse> {
    return (await apiClient.delete(`/signals/${id}`)).data;
  },

  /**
   * Get related signals for an issue/goal
   */
  async getRelatedSignals(
    data: RelatedSignalsApiRequest
  ): Promise<RelatedSignalsApiResponse[]> {
    const response = await apiClient.post('/signals/related', data, {
      timeout: 30000,
    });

    return response.data;
  },

  /**
   * Generate signal suggestions from uploaded Word files
   */
  async generateSignalsFromFile(
    files: File[]
  ): Promise<GenerateSignalsFromFileApiResponse> {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }

    return (
      await apiClient.post('/signals/from-file', formData, {
        timeout: 100000,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    ).data;
  },

  async activateSignal(
    id: string,
    scope?: SignalApplicabilityScope
  ): Promise<ActivateSignalApiResponse> {
    return (
      await apiClient.post(`/signals/${id}/activate`, scope ? { scope } : {})
    ).data;
  },

  async deactivateSignal(id: string): Promise<DeactivateSignalApiResponse> {
    return (await apiClient.post(`/signals/${id}/deactivate`)).data;
  },

  async applySignal(
    id: string,
    scope: SignalApplicabilityScope
  ): Promise<ApplySignalApiResponse> {
    return (await apiClient.post(`/signals/${id}/apply`, { scope })).data;
  },

  async unapplySignal(
    id: string,
    scope: SignalApplicabilityScope
  ): Promise<UnapplySignalApiResponse> {
    return (await apiClient.post(`/signals/${id}/unapply`, { scope })).data;
  },

  async changeSignalScope(
    id: string,
    newScope: SignalApplicabilityScope
  ): Promise<Signal> {
    return (
      await apiClient.post(`/signals/${id}/change-scope`, {
        newScope,
      })
    ).data;
  },

  async getMarketplaceSignals(): Promise<GetMarketplaceSignalsApiResponse> {
    return (await apiClient.get('/signals/marketplace')).data;
  },

  async importSignal(id: string): Promise<ImportSignalApiResponse> {
    return (await apiClient.post(`/signals/${id}/import`)).data;
  },

  async removeImportedSignal(
    id: string
  ): Promise<RemoveImportedSignalApiResponse> {
    return (await apiClient.post(`/signals/${id}/remove`)).data;
  },

  async getRelatedIssues(
    signalId: string
  ): Promise<GetSignalRelatedIssuesApiResponse> {
    return (await apiClient.get(`/signals/${signalId}/related-issues`)).data;
  },
};
